/**
 * whiteboard slice: Excalidraw 화이트보드 상태 관리 (/video-renewal 라우트 전용)
 *
 * - 기존 drawingSlice, documentShareSlice는 /video 라우트(Fabric.js)용으로 보존
 * - 이 슬라이스는 /video-renewal에서 스케치 모드와 문서공유 모드를 모두 담당
 * - v1 원본: redux/modules/whiteboard.js
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CollabDetails, FileMetadata, PageCollabDetail, DocShareFile } from '@/types/whiteboard';

// re-export for backwards compatibility
export type { CollabDetails, FileMetadata, PageCollabDetail, DocShareFile } from '@/types/whiteboard';

export interface WhiteboardState {
  /** 화이트보드 활성 여부 */
  isOpen: boolean;
  /** true: 스케치 모드, false: 문서공유 모드 */
  isSketchMode: boolean;

  /** 화이트보드 오너 Jitsi ID */
  ownerId: string | null;
  /** 내가 오너인지 */
  isOwner: boolean;

  /** 스케치 코치마크 표시 여부 */
  isSketchCoachMarkShown: boolean;

  /** 현재 페이지 (0-indexed) */
  currentPage: number;
  /** 전체 페이지 수 */
  totalPages: number;

  /** Excalidraw 배경 설정 */
  backgroundProps: {
    viewBackgroundColor: string;
    gridModeEnabled: boolean;
    gridSize: number;
  };

  /** 협업 서버 연결 정보 */
  collabDetails: CollabDetails | null;

  /** 문서공유 파일 정보 */
  fileMetadata: FileMetadata | null;

  /** 공유 PDF (URL 또는 base64) */
  sharedPdf: string | null;

  /** 사용자별 현재 페이지 추적 */
  userPages: Record<string, number>;

  /** 페이지별 드로잉 데이터 */
  pageDrawingData: Record<number, unknown>;

  /** 문서공유: 페이지별 collabDetails 목록 */
  collabDetailList: PageCollabDetail[];

  /** 문서공유: 파일 데이터 (이미지 URL 등) */
  docShareFiles: DocShareFile[];

  /** 문서공유/회의자료 파일 로딩 상태 */
  isFileLoading: boolean;
}

const initialState: WhiteboardState = {
  isOpen: false,
  isSketchMode: true,
  ownerId: null,
  isOwner: false,
  isSketchCoachMarkShown: false,
  currentPage: 0,
  totalPages: 1,
  backgroundProps: {
    viewBackgroundColor: '#ffffff',
    gridModeEnabled: false,
    gridSize: 20,
  },
  collabDetails: null,
  fileMetadata: null,
  sharedPdf: null,
  userPages: {},
  pageDrawingData: {},
  collabDetailList: [],
  docShareFiles: [],
  isFileLoading: false,
};

const whiteboardSlice = createSlice({
  name: 'whiteboard',
  initialState,
  reducers: {
    openWhiteboard(
      state,
      action: PayloadAction<{
        ownerId: string;
        isSketchMode: boolean;
        collabDetails: CollabDetails;
        isOwner: boolean;
      }>
    ) {
      state.isOpen = true;
      state.ownerId = action.payload.ownerId;
      state.isSketchMode = action.payload.isSketchMode;
      state.collabDetails = action.payload.collabDetails;
      state.isOwner = action.payload.isOwner;
      // 스케치 모드로 열 때 코치마크 초기화 (첫 번 표시하도록)
      if (action.payload.isSketchMode) {
        state.isSketchCoachMarkShown = false;
      }
    },

    closeWhiteboard() {
      return { ...initialState };
    },

    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },

    setTotalPages(state, action: PayloadAction<number>) {
      state.totalPages = action.payload;
    },

    setBackgroundProps(
      state,
      action: PayloadAction<Partial<WhiteboardState['backgroundProps']>>
    ) {
      state.backgroundProps = { ...state.backgroundProps, ...action.payload };
    },

    setFileMetadata(state, action: PayloadAction<FileMetadata | null>) {
      state.fileMetadata = action.payload;
    },

    setSharedPdf(state, action: PayloadAction<string | null>) {
      state.sharedPdf = action.payload;
    },

    setUserPage(state, action: PayloadAction<{ userId: string; page: number }>) {
      state.userPages[action.payload.userId] = action.payload.page;
    },

    setPageDrawingData(state, action: PayloadAction<{ page: number; data: unknown }>) {
      state.pageDrawingData[action.payload.page] = action.payload.data;
    },

    setCollabDetailList(state, action: PayloadAction<PageCollabDetail[]>) {
      state.collabDetailList = action.payload;
    },

    setDocShareFiles(state, action: PayloadAction<DocShareFile[]>) {
      state.docShareFiles = action.payload;
    },

    setSketchCoachMarkShown(state, action: PayloadAction<boolean>) {
      state.isSketchCoachMarkShown = action.payload;
    },

    setIsFileLoading(state, action: PayloadAction<boolean>) {
      state.isFileLoading = action.payload;
    },

    reset() {
      return { ...initialState };
    },
  },
});

export const whiteboardActions = whiteboardSlice.actions;
export default whiteboardSlice.reducer;
